import { skipToken } from '@tanstack/react-query';
import { sortBy } from '../../../shared/utils/sortUtils.ts';
import { trpc } from '../../utils/trpc/trpc.ts';


interface Props {
    equipment: string;
    product: string | null;
    partName: string | null;
}

export function useLoadRecords({
    equipment,
    product,
    partName,
}: Props) {

    const { data: batches } = trpc.batches.get.useQuery(
        (product || partName) ? {
            equipment,
            ...(product ? { product } : undefined),
            ...(partName ? { partName } : undefined),
        } : skipToken,
        {
            select: batches => sortBy([...batches], it => it.start),
            refetchOnWindowFocus: false,
        }
    );


    const recordQueries = trpc.useQueries((t) =>
        batches?.map(batch =>
            t.records.getWithProtocols(
                {
                    equipment,
                    measurementStart: { start: batch.start, end: batch.end ?? undefined },
                    isDraft: false,
                    sort: { measurementStart: 1 }
                },
                { refetchOnWindowFocus: false }
            )
        ) ?? []
    );

    const allRecordsLoaded = recordQueries.length && recordQueries.every(it => it.isFetched);
    const recordsPerBatch = allRecordsLoaded ? recordQueries.map(it => it.data ?? []) : null;
    const allRecords = recordsPerBatch ? recordsPerBatch.flat() : null;
    const latestRecord = allRecords?.at(-1);


    const { data: protocol } = trpc.protocols.getById.useQuery(
        latestRecord?.protocolId ?? skipToken,
        { refetchOnWindowFocus: false, }
    );

    const operationQueries = trpc.useQueries((t) =>
        batches?.map(batch =>
            t.operations.get({
                equipment,
                effectiveTime: { start: batch.start, end: batch.end },
                operationType: 'Tool change',
            }),
            { refetchOnWindowFocus: false }
        ) ?? [],
    );

    const allOperationsLoaded = operationQueries.length && operationQueries.every(it => it.isFetched);
    const operationsPerBatch = allOperationsLoaded ? operationQueries.map(it => it.data ?? []) : null;
    const allOperations = operationsPerBatch ? operationsPerBatch.flat() : null;

    return {
        allRecords,
        recordsPerBatch,
        protocol,
        batches,
        allOperations,
        operationsPerBatch,
    };
}
